/**
 * Форма темы набора (#845): название (≤ 60) и что в неё входит (≤ 80). Одна на
 * добавление и правку.
 *
 * «Тема ≠ категория» (спека §2.1): название, повторяющее категорию звонка или
 * предмет вопроса клиента, ПРЕДУПРЕЖДАЕТ, но не запрещает сохранить —
 * совпадение считает та же таблица `@aica/core`, что и сервер, по мере ввода.
 *
 * Оба поля проверяются тем же контрактом, что у сервера (`themeFieldProblem`):
 * причина отказа — словами под полем, а не молча выключенная кнопка.
 */
import { useId, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { findThemeOverlaps } from '@aica/core/voc/theme-overlap';
import { ORG_THEME_DEFINITION_MAX, ORG_THEME_NAME_MAX } from '@aica/api-types/schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { overlapNames } from './overlap-names';
import { themeFieldProblem, type ThemeFieldProblem } from './theme-field-problem';

export interface VoiceThemeFormValues {
  name: string;
  definition: string;
}

interface VoiceThemeFormProps {
  initial?: VoiceThemeFormValues;
  submitLabel: string;
  /** Resolves on success; a rejection's message is shown next to the button. */
  onSubmit: (values: VoiceThemeFormValues) => Promise<void>;
  onCancel?: () => void;
  /** Clear the fields after a successful submit (the «add» form). */
  resetOnSuccess?: boolean;
}

const EMPTY: VoiceThemeFormValues = { name: '', definition: '' };

export function VoiceThemeForm({
  initial = EMPTY,
  submitLabel,
  onSubmit,
  onCancel,
  resetOnSuccess,
}: VoiceThemeFormProps) {
  const { t } = useTranslation(['dashboard', 'records']);
  const id = useId();
  const [values, setValues] = useState(initial);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nameProblem = themeFieldProblem('name', values.name);
  const definitionProblem = themeFieldProblem('definition', values.definition);
  const valid = nameProblem === null && definitionProblem === null;
  /** The reason in words — none for a name not typed yet. */
  const problemText = (problem: ThemeFieldProblem | null, max: number): string | null =>
    problem === null || problem === 'empty'
      ? null
      : t(`dashboard:voicePage.themes.form.problems.${problem}`, { max });
  const nameMessage = problemText(nameProblem, ORG_THEME_NAME_MAX);
  const definitionMessage = problemText(definitionProblem, ORG_THEME_DEFINITION_MAX);
  // The live §2.1 hint only for a name being typed: an existing theme opened
  // for an edit of its definition is not re-warned about the name it has.
  const overlaps = useMemo(
    () => (values.name === initial.name ? [] : findThemeOverlaps(values.name)),
    [values.name, initial.name]
  );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid || pending) return;
    setPending(true);
    setError(null);
    try {
      await onSubmit({ name: values.name.trim(), definition: values.definition.trim() });
      if (resetOnSuccess) setValues(EMPTY);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setPending(false);
    }
  };

  return (
    <form className="space-y-3" onSubmit={(event) => void submit(event)}>
      <div className="space-y-1.5">
        <label
          htmlFor={`${id}-name`}
          className="block text-sm font-medium text-fg-secondary"
        >
          {t('dashboard:voicePage.themes.form.name')}
        </label>
        <Input
          id={`${id}-name`}
          value={values.name}
          maxLength={ORG_THEME_NAME_MAX}
          placeholder={t('dashboard:voicePage.themes.form.namePlaceholder')}
          onChange={(event) => setValues({ ...values, name: event.target.value })}
          disabled={pending}
          error={nameMessage !== null}
          aria-invalid={nameMessage !== null}
          aria-describedby={
            nameMessage !== null
              ? `${id}-name-problem`
              : overlaps.length > 0
                ? `${id}-overlap`
                : undefined
          }
        />
        {nameMessage && (
          <p id={`${id}-name-problem`} className="text-xs text-danger">
            {nameMessage}
          </p>
        )}
      </div>
      <div className="space-y-1.5">
        <label
          htmlFor={`${id}-definition`}
          className="block text-sm font-medium text-fg-secondary"
        >
          {t('dashboard:voicePage.themes.form.definition')}
        </label>
        <Input
          id={`${id}-definition`}
          value={values.definition}
          maxLength={ORG_THEME_DEFINITION_MAX}
          placeholder={t('dashboard:voicePage.themes.form.definitionPlaceholder')}
          onChange={(event) => setValues({ ...values, definition: event.target.value })}
          disabled={pending}
          error={definitionMessage !== null}
          aria-invalid={definitionMessage !== null}
          aria-describedby={
            definitionMessage !== null ? `${id}-definition-problem` : undefined
          }
        />
        {definitionMessage && (
          <p id={`${id}-definition-problem`} className="text-xs text-danger">
            {definitionMessage}
          </p>
        )}
        <p className="text-xs text-fg-muted">
          {t('dashboard:voicePage.themes.form.definitionHint')}
        </p>
      </div>

      {overlaps.length > 0 && (
        <p
          id={`${id}-overlap`}
          role="status"
          className="flex items-start gap-2 rounded-card border border-warning/30 bg-warning-dim px-3 py-2 text-sm text-warning-fg"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {t('dashboard:voicePage.themes.form.overlapWarning', {
              names: overlapNames(overlaps, t),
            })}
          </span>
        </p>
      )}

      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={!valid || pending}>
          {submitLabel}
        </Button>
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={pending}
          >
            {t('dashboard:voicePage.themes.form.cancel')}
          </Button>
        )}
      </div>
    </form>
  );
}
